import React, { createContext, useState, useContext, ReactNode } from 'react';
import talentService, { TalentSearchFilters, TalentDetails } from '../services/api/talentService';
import { SearchResponse } from '../services/api/talentService';
import { toast } from 'react-toastify';

interface TalentContextType {
	// AI Search state
	sessionId: string;
	searchQuery: string;
	inputValue: string;
	isSearching: boolean;
	hasSearched: boolean;
	searchResults: SearchResponse | null;
	error: string | null;

	// Classic Search state
	classicSearchText: string;
	classicMaxHourlyRate: number | undefined;
	classicAvailableBefore: string | undefined;
	isClassicSearching: boolean;
	hasClassicSearched: boolean;
	classicSearchResults: TalentDetails[] | null;
	classicSearchError: string | null;

	// AI Search Actions
	setInputValue: (value: string) => void;
	setSearchQuery: (query: string) => void;
	handleSearch: (query: string, organizationId: string) => Promise<void>;
	resetSearch: () => void;

	// Classic Search Actions
	setClassicSearchText: (value: string) => void;
	setClassicMaxHourlyRate: (value: number | undefined) => void;
	setClassicAvailableBefore: (value: string | undefined) => void;
	handleClassicSearch: (organizationId: string) => Promise<void>;
	resetClassicSearch: () => void;
	goBackToClassicSearch: () => void;
}

interface TalentProviderProps {
	children: ReactNode;
}

// Create the context with a default value
const TalentContext = createContext<TalentContextType | undefined>(undefined);

// Provider component
export const TalentProvider: React.FC<TalentProviderProps> = ({ children }) => {
	// AI Search state
	const [sessionId, setSessionId] = useState<string>(talentService.generateUUID());
	const [searchQuery, setSearchQuery] = useState<string>('');
	const [inputValue, setInputValue] = useState<string>('');
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [hasSearched, setHasSearched] = useState<boolean>(false);
	const [searchResults, setSearchResults] = useState<SearchResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Classic Search state
	const [classicSearchText, setClassicSearchText] = useState<string>('');
	const [classicMaxHourlyRate, setClassicMaxHourlyRate] = useState<number | undefined>(undefined);
	const [classicAvailableBefore, setClassicAvailableBefore] = useState<string | undefined>(
		undefined,
	);
	const [isClassicSearching, setIsClassicSearching] = useState<boolean>(false);
	const [hasClassicSearched, setHasClassicSearched] = useState<boolean>(false);
	const [classicSearchResults, setClassicSearchResults] = useState<TalentDetails[] | null>(null);
	const [classicSearchError, setClassicSearchError] = useState<string | null>(null);

	// Handle AI search action
	const handleSearch = async (query: string, organizationId: string): Promise<void> => {
		if (!organizationId || !query.trim() || !sessionId) return;

		setIsSearching(true);
		setError(null);
		setSearchQuery(query);

		try {
			const response = await talentService.searchTalentsWithCopilot(
				organizationId,
				sessionId,
				query,
			);
			setSearchResults(response);
			setHasSearched(true);
		} catch (err) {
			setSearchResults(null);
			setError('Failed to search talents');
			toast.error('Failed to search talents');
			console.error(err);
		} finally {
			setIsSearching(false);
		}
	};

	// Handle classic search action
	const handleClassicSearch = async (organizationId: string): Promise<void> => {
		if (!organizationId || !classicSearchText.trim()) return;

		setIsClassicSearching(true);
		setClassicSearchError(null);

		try {
			const filters: TalentSearchFilters = {
				searchText: classicSearchText.trim(),
				maxHourlyRate: classicMaxHourlyRate,
				availableBefore: classicAvailableBefore,
			};

			const response = await talentService.searchTalentsWithFilters(organizationId, filters);
			setClassicSearchResults(response);
			setHasClassicSearched(true);
		} catch (err) {
			setClassicSearchResults(null);
			setClassicSearchError('Failed to search talents');
			toast.error('Failed to search talents');
			console.error(err);
		} finally {
			setIsClassicSearching(false);
		}
	};

	// Reset AI search state
	const resetSearch = (): void => {
		setHasSearched(false);
		setSearchResults(null);
		setSearchQuery('');
		setInputValue('');
		// Generate a new session ID for a fresh search
		setSessionId(talentService.generateUUID());
	};

	// Reset classic search state
	const resetClassicSearch = (): void => {
		setHasClassicSearched(false);
		setClassicSearchResults(null);
		setClassicSearchText('');
		setClassicMaxHourlyRate(undefined);
		setClassicAvailableBefore(undefined);
		setClassicSearchError(null);
	};

	// Go back to classic search input without losing results
	const goBackToClassicSearch = (): void => {
		setHasClassicSearched(false);
	};

	// The value that will be available to consumers of the context
	const value: TalentContextType = {
		// AI Search
		sessionId,
		searchQuery,
		inputValue,
		isSearching,
		hasSearched,
		searchResults,
		error,
		setInputValue,
		setSearchQuery,
		handleSearch,
		resetSearch,
		// Classic Search
		classicSearchText,
		classicMaxHourlyRate,
		classicAvailableBefore,
		isClassicSearching,
		hasClassicSearched,
		classicSearchResults,
		classicSearchError,
		setClassicSearchText,
		setClassicMaxHourlyRate,
		setClassicAvailableBefore,
		handleClassicSearch,
		resetClassicSearch,
		goBackToClassicSearch,
	};

	return <TalentContext.Provider value={value}>{children}</TalentContext.Provider>;
};

// Custom hook to use the talent context
export const useTalentContext = (): TalentContextType => {
	const context = useContext(TalentContext);
	if (context === undefined) {
		throw new Error('useTalentContext must be used within a TalentProvider');
	}
	return context;
};

export default TalentContext;
